import { CategoryProps } from '@gc-bridge/models';
import { getRemoteApplications } from '@gcportal/base';
import { AdditionalColors } from '@gc-bridge/theme/colors';
import { Box, BoxProps, Typography, TypographyProps, styled } from '@mui/material';
import { useMemo } from 'react';
import { Remote } from './Remote';

const CardContainer = styled(Box)({
  display: 'grid',
  gridTemplateRows: 'minmax(300px, 1fr) 1fr',
  gap: '8px',
  backgroundColor: 'transparent',
  height: '100%',
  overflow: 'hidden',
});

const Picture = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'image',
})<BoxProps & { image: string }>(({ image }) => ({
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  paddingTop: '150px',
}));

const CardHeaderContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: '12px',
  padding: '0 8%',
});

const CardBodyContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const Title = styled(Typography)<TypographyProps>(({ theme: { palette } }) => ({
  fontFamily: 'Lato',
  fontWeight: 700,
  fontSize: '26px',
  lineHeight: '100%',
  letterSpacing: '0%',
  color: palette.primary.contrastText,
  textShadow: `2px 2px 6px ${AdditionalColors.Black}`,
}));

const Description = styled(Typography)<TypographyProps>(({ theme: { palette } }) => ({
  flex: 1,
  fontFamily: 'Lato',
  fontWeight: 400,
  fontSize: '18px',
  lineHeight: '100%',
  letterSpacing: '0%',
  color: palette.primary.contrastText,
  textShadow: `2px 2px 6px ${AdditionalColors.Black}`,
}));

interface Props {
  category: CategoryProps;
}

export function Category({ category }: Props) {
  const remotes = useMemo(
    () =>
      getRemoteApplications()
        .filter((app) => app.category === category.id),
    [category.id]
  );

  return (
    <CardContainer>
      <Picture image={category.picture}>
        <CardHeaderContent>
          <Title>{category.title}</Title>
          {category.description && <Description>{category.description}</Description>}
        </CardHeaderContent>
      </Picture>
      <CardBodyContent>
        {remotes.map((remote) => (
          <Remote key={remote.name} remote={remote} />
        ))}
      </CardBodyContent>
    </CardContainer>
  );
}
